import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext } from 'react';
import { useStore as useStoreNative } from 'zustand';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { useBIRelationshipScope } from '~/providers/BIRelationshipScopeProvider';
const StoreContext = /*#__PURE__*/ createContext(undefined);
export function useStore(selector) {
    const store = useContext(StoreContext);
    if (!store) {
        throw new Error('useStore must be used within a StoreProvider');
    }
    return useStoreNative(store, selector);
}
export function useStoreStatic() {
    const store = useContext(StoreContext);
    if (!store) {
        throw new Error('useStoreStatic must be used within a StoreProvider');
    }
    return {
        getState: store.getState
    };
}
/**
 * Same as useStore but with built-in shallow object/array comparison. Use this hook
 * if you want to construct a single object/array with multiple state-picks inside,
 * similar to redux's mapStateToProps.
 * @see https://github.com/pmndrs/zustand#selecting-multiple-state-slices
 */ export function useShallowStore(selector) {
    const store = useContext(StoreContext);
    if (!store) throw new Error('useShallowStore must be used within a StoreProvider');
    return useStoreWithEqualityFn(store, selector, shallow);
}
/**
 * A convenience method that returns the actions from the store.
 * It's configured in eslint to be static, so the actions don't need
 * to be added to the dependency array in hooks that use them.
 */ export const useStoreActions = ()=>{
    const relationshipScope = useBIRelationshipScope();
    const actions = useStore(actionsSelector);
    const relationshipScopedLogBI = (id, data)=>actions.logBI(id, _object_spread_props(_object_spread({}, data), {
            relationshipId: relationshipScope && relationshipScope.id,
            isApprovedRelationship: relationshipScope && relationshipScope.isApproved
        }));
    return _object_spread_props(_object_spread({}, actions), {
        logBI: relationshipScopedLogBI
    });
};
const actionsSelector = (state)=>state.actions;
export function withStoreActions(Component) {
    const WithStoreActions = (props)=>{
        const actions = useStoreActions();
        return /*#__PURE__*/ _jsx(Component, _object_spread_props(_object_spread({}, props), {
            actions: actions
        }));
    };
    return WithStoreActions;
}
export const Provider = StoreContext.Provider;
export function useAppConfig() {
    return useStore(appConfigSelector);
}
const appConfigSelector = (state)=>state.appConfig;
